import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import Container from "../../components/Container";
import Header from "../../components/Header";
import Cta from "../../components/Cta";
import ArrowLink from "../../components/ArrowLink";
import Layout from "../../components/layout";
import Footer from "../../components/Footer";
import Heading from "../../components/Heading";
import { getResources, getCustomOptions } from "../../lib/api";
import PostTitle from "../../components/post-title";
import ResourcesCard from "../../components/ResourcesCard";
import ResourcesHero from "../../components/ResourcesHero";
import ResourcesSearch from "../../components/ResourcesSearch";
import ResourcesFilters from "../../components/ResourcesFilters";
import ResourcesPagination from "../../components/ResourcesPagination";
import styles from "./styles.module.css";

export default function Resources({ resources, preview, footerData, ctaData }) {
  const router = useRouter();
  const page = parseInt(router.query.page, 10) || 1;
  const perPage = 3;

  const [searchPhrase, setSearchPhrase] = useState("");
  const [filteredResources, setFilteredResources] = useState(resources);
  const [filter, setFilter] = useState(null);

  const onSearch = (e) => {
    setSearchPhrase(e.target.value);
  };

  useEffect(() => {
    getResources(searchPhrase).then((data) => {
      setFilteredResources(data);
    });
  }, [searchPhrase]);

  const resourcesAfterFilters = filteredResources.nodes.filter((resource) => {
    if (filter === null) {
      return true;
    }
    if (!resource.categories.nodes.length) {
      return false;
    }
    const firstCategory = resource.categories.nodes[0].slug;
    if (filter === "media") {
      return ["podcast", "webinar", "press-release", "news-feature"].includes(
        firstCategory
      );
    }
    return firstCategory === filter;
  });

  const paginateResources = (resources) => {
    if (filter === null && searchPhrase === "") {
      return resources
        .slice(1)
        .slice((page - 1) * perPage, (page - 1) * perPage + perPage);
    } else {
      return resources;
    }
  };

  const ResourcesList = () => {
    return resourcesAfterFilters.length ? (
      <div className={styles.resourcesGrid}>
        {paginateResources(resourcesAfterFilters).map((resource) => (
          <ResourcesCard key={resource.slug} {...resource} />
        ))}
      </div>
    ) : (
      <div className={styles.noResults}>
        <Heading size="big">
          No results
        </Heading>
      </div>
    )
  }

  return (
    <Layout preview={preview}>
      <div className={styles.resources}>
        <Header isDark={true} />
        {router.isFallback ? (
          <PostTitle>Loading…</PostTitle>
        ) : (
          <>
            <Container isWide={true}>
              {resources.nodes.length && (
                <ResourcesHero
                  {...resources.nodes[0]}
                  label={resources.nodes[0].commonFields.label}
                />
              )}
            </Container>
            <Container className={styles.container}>
              <div className={styles.top}>
                <ResourcesFilters filter={filter} setFilter={setFilter} />
                <ResourcesSearch value={searchPhrase} onChange={onSearch} />
              </div>

              {searchPhrase !== "" && !resourcesAfterFilters.length ? (
                <div className={styles.noResults}>
                  <Heading size="big">
                    No search results for: “{searchPhrase}”
                  </Heading>
                  <ArrowLink onClick={() => setSearchPhrase("")}>
                    Back to all resources
                  </ArrowLink>
                </div>
              ) : (
                <ResourcesList />
              )}

              {!!filteredResources.nodes.length &&
                filter === null &&
                searchPhrase === "" && (
                  <ResourcesPagination
                    activePage={page}
                    pages={Math.ceil((resources.nodes.length - 1) / perPage)}
                  />
                )}
            </Container>
          </>
        )}
        {ctaData && (
          <Cta
            title={ctaData.title}
            subtitle={ctaData.subtitle}
            icon={ctaData.icon}
            button={ctaData.button}
          />
        )}
      </div>
      {footerData && <Footer data={footerData} />}
    </Layout>
  );
}

export async function getStaticProps({ params, preview = false, previewData }) {
  const resources = await getResources(null, preview, previewData);
  const { footer, cta } = await getCustomOptions(preview);
  return {
    props: {
      resources,
      preview,
      footerData: footer,
      ctaData: cta,
    },
    revalidate: 10,
  };
}
